// When the app stops on its own welcome page instead of going straight to the sign-in (TF-239).
// Without a session every page hands over to Keycloak at once. The welcome page is only for a person
// who just signed out, and for a sign-in that came back with an error, where going round again would
// loop: it says which, and the Sign in button starts over. Kept per tab (sessionStorage), like the
// tokens.

export type SignInStop = 'signedOut' | 'cancelled' | 'expired' | 'failed';

const STOP_KEY = 'teamfolio.signInStop';
const TRIES_KEY = 'teamfolio.signInTries';
const STOPS: readonly SignInStop[] = ['signedOut', 'cancelled', 'expired', 'failed'];
/** Hand-overs to Keycloak within this time that came back without a session: then stop, not loop. */
const TRIES_WINDOW_MS = 2 * 60_000;
const MAX_TRIES = 2;

function read(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}
function write(key: string, value: string | null): void {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    // Storage blocked: the welcome page simply does not remember, and the button still works.
  }
}

export function readStop(): SignInStop | null {
  const value = read(STOP_KEY);
  return STOPS.includes(value as SignInStop) ? (value as SignInStop) : null;
}
export const rememberStop = (stop: SignInStop) => write(STOP_KEY, stop);
/** A deliberate sign-in (the button, /signin) or a session that arrived: start afresh. */
export function clearStop(): void {
  write(STOP_KEY, null);
  write(TRIES_KEY, null);
}

/**
 * Counts an automatic hand-over to Keycloak. False when this tab already went round MAX_TRIES times
 * in a short while and came back without a usable session (a clock far off, say): then the welcome
 * page asks, rather than the browser bouncing between the app and Keycloak for ever.
 */
export function mayHandOver(now = Date.now()): boolean {
  const recent = (read(TRIES_KEY) ?? '')
    .split(',')
    .map(Number)
    .filter((at) => Number.isFinite(at) && now - at < TRIES_WINDOW_MS);
  if (recent.length >= MAX_TRIES) return false;
  write(TRIES_KEY, [...recent, now].join(','));
  return true;
}

/** Why Keycloak's answer did not sign anyone in, from the error oidc-client-ts throws. */
export function stopOf(error: unknown): SignInStop {
  const code = typeof error === 'object' && error !== null && 'error' in error ? String((error as { error: unknown }).error) : '';
  if (code === 'access_denied' || code === 'login_required' || code === 'consent_required' || code === 'interaction_required') return 'cancelled';
  const message = error instanceof Error ? error.message : '';
  // The sign-in was started in another tab, opened twice, or left so long that its state is gone.
  if (code === 'invalid_grant' || code === 'temporarily_unavailable' || /state/i.test(message)) return 'expired';
  return 'failed';
}

const EMAIL = /^[^\s@<>"]+@[^\s@<>"]+\.[^\s@<>"]+$/;

/**
 * What the sign-in page is told, beyond the protocol: the language to show (`ui_locales`) and, when
 * the person typed it on the website, the e-mail to fill in (`login_hint`). Anything not plausible is
 * left out rather than shown to Keycloak.
 */
export function signInHints(hints: { language?: string | null; email?: string | null }): Record<string, string> {
  const params: Record<string, string> = {};
  const language = hints.language?.trim().toLowerCase();
  if (language && /^[a-z]{2,3}(-[a-z0-9]{2,8})?$/.test(language)) params.ui_locales = language;
  const email = hints.email?.trim();
  if (email && email.length <= 254 && EMAIL.test(email)) params.login_hint = email;
  return params;
}
